import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { CustomerProfile, MarketingChannel, MarketingContext, LoyaltyProfile } from '@/models';
import { getSignInPath, Routes } from '@/router';
import { formatPhoneNumber, calculateAge } from '@/helpers';
import moment from 'moment';
import { CustomerError } from '@/services/customer/customer.service';
import { EnrollmentOptions } from '@/services/loyalty/loyalty.service';
import { CountryCode } from '@/store/ui';
import { AccountManagementEvent, AccountPageLoadEvent } from '@/services/analytics/events';
import { InteractionTrackingType } from '@/services/analytics/platforms/adobe-analytics/models';
import { AUTOMATIC_PERSONALIZATION_AGE } from '@/constants'
import useAccountPage from '@/composables/useAccountPage';

@Component({
  name: 'CmgPersonalAndPreferencesPage',
})
export default class CmgPersonalAndPreferencesPage extends Vue {
  subscribeToEmail = false;
  subscribeToText = false;
  subscribeToPersonalization = false;
  isJoiningRewards = false;
  isSubscribingEmail = false;
  isSubscribingText = false;
  isSubscribingPersonalization = false;
  hasSubscriptionError = false;
  errorMessage = '';
  loyaltySubscriptionStartDate = '';
  loyaltyMembershipId = '';

  async mounted() {
    await this.$services.config.setLegalText();
    this.$services.customer.setProfileSubscriptionChangeOnly(true);
    this.$services.customer.refreshCustomerProfileInfo(true, false, true);

    if (!(await this.$services.customer.waitForCustomerInitialization())) {
      this.$router.push(getSignInPath(this.$route.path));
    }
  }

  async activated() {
    // Called when component is activated (e.g., shown in <keep-alive>)
    useAccountPage({ isModal: true }, this.$store, this.$services.customer, { router: this.$router });
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get isCanadian() {
    return this.customer && this.customer.country == CountryCode.CA;
  }

  get isUKCustomer() {
    return this.customer && (this.customer.country == CountryCode.GB || this.customer.country == CountryCode.UK);
  }

  get isLoyaltyEligible(): boolean {
    return this.$services.loyalty.isLoyaltyEligible(this.$appStore.state.customer.profile);
  }

  get loyaltyProfile(): LoyaltyProfile | null {
    if (!this.customer) {
      return null;
    }

    return this.$services.loyalty.getLoyaltyProfile(this.customer);
  }

  get customerProfile() {
    return this.$appStore.state.customer.profile;
  }

  get hasGraduationDate(): boolean {
    return Boolean(this.customer?.student?.graduationDate);
  }

  get studentGraduationDate(): string {
    if (!this.customer?.student?.graduationDate) return '';
    return moment(this.customer.student.graduationDate).format('MM/DD/YYYY');
  }

  get isCustomerStudent(): boolean {
    return this.$appStore.getters.isActiveStudent;
  }

  get isBirthdayDisabled(): boolean {
    const { userVerifiedBirthDate, externallyConfirmed18, dateOfBirth } = this.customer || {};
    return !this.isCountryUK && ((userVerifiedBirthDate !== undefined && userVerifiedBirthDate) || (externallyConfirmed18 !== undefined && externallyConfirmed18 && dateOfBirth !== undefined));
  }

  get dateFormat(): string {
    return !this.isCountryUK ? 'MMDDYYYY' : 'DDMM';
  }

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get isCountryUK(): boolean {
    return this.countryCode === CountryCode.GB;
  }

  get isEnrolledInLoyalty(): boolean {
    if (!this.customer) {
      return false;
    }

    const profile = this.$services.loyalty.getLoyaltyProfile(this.customer);
    return profile && profile.memberId ? true : false;
  }

  get fullCustomerName(): string {
    if (!this.customer) {
      return '';
    }

    return `${this.customer.firstName || ''} ${this.customer.lastName}`;
  }

  get phoneNumber(): string {
    if (!this.customer) {
      return '';
    }

    return formatPhoneNumber(this.customer.phoneNumber, this.customer.country);
  }

  get email(): string {
    if (!this.customer) {
      return '';
    }

    return this.customer.email;
  }

  get formattedEmail(): string {
    if (!this.customer) {
      return '';
    }
    const fullEmail = this.customer.email;
    if (!fullEmail || fullEmail.length < 48) {
      return this.customer.email;
    }
    const start = fullEmail.substring(0, 22);
    const end = fullEmail.substring(fullEmail.length - 22);
    return `${start}...${end}`;
  }

  get birthday(): string {
    if (!this.customer || !this.customer.dateOfBirth) {
      return '';
    }

    const dateFormat = this.customer.country !== CountryCode.GB ? 'MMMM DD' : 'DD MMMM';
    return moment(this.customer.dateOfBirth).format(dateFormat);
  }

  get zip(): string {
    if (!this.customer || !this.customer.zip) {
      return '';
    }

    return this.customer.zip;
  }

  get infoIconImageUrl(): string {
    return this.$appStore.state.cms.images.icons.infoIcon.default;
  }

  get emailUpdatesText(): string {
    return this.$services.config.getJoinRewards() ?? "<p>You'll also get email updates which you can turn off at any time.</p>\n";
  }

  get legalCopy(): string {
    return this.$services.config.getLegalText().personalAndPreference;
  }

  get country(): string {
    if (this.customer && this.customer.country) {
      switch (this.customer.country) {
        case CountryCode.US:
          return 'United States';
        case CountryCode.CA:
          return 'Canada';
        case CountryCode.GB:
          return 'United Kingdom';
      }
    }
    return '';
  }

  get dietsAndAllergensSymbol(): string {
    return this.$appStore.state.cms.images.icons.nutritionFilter.default;
  }

  get selectTagsText(): string {
    return this.hasDietaryTagsSet ? 'Change' : 'Select Tags';
  }

  get confirmYourBirhtdayText(): string {
    return 'We updated our <b><a href="/privacy-policy">Privacy Policy</a></b>, <a href="/rewards-terms"><b>Rewards Terms</b></a>, and <b><a href="/terms-of-use">Terms of Use</a></b>.</br> You’ll need to update some personal info.'
  }

  get hasDietaryTagsSet(): boolean {
    return this.customer?.dietaryTags != null && this.customer.dietaryTags.length > 0;
  }

  get showPersonalizationOption() {
    if (this.customer?.dateOfBirth) {
      const age = calculateAge(this.customer?.dateOfBirth);
      if (age < AUTOMATIC_PERSONALIZATION_AGE) {
        return true;
      }
    }
    return false;
  }

  get birthdayConfirmationRequired(): boolean {
    const { userVerifiedBirthDate, externallyConfirmed18 } = this.customer || {};
    return !this.isCountryUK && !(userVerifiedBirthDate || externallyConfirmed18);
  }

  setloyaltyMembershipId() {
    this.loyaltyMembershipId = (this.loyaltyProfile && this.loyaltyProfile.memberId) || '';
  }

  setloyaltySubscriptionStartDate() {
    if (!this.loyaltyProfile) {
      return;
    }

    this.loyaltySubscriptionStartDate = moment(this.loyaltyProfile.subscribeDate).format('YY');
  }

  @Watch('customer', { immediate: true, deep: true })
  onCustomerUpdated(newCustomer: CustomerProfile | null) {
    if (!newCustomer && !this.$appStore.state.customer.jwt) {
      this.$router.push({ name: Routes.Home, hash: '#menu' }).catch((err: any) => {
        if (err.name !== 'NavigationDuplicated') throw err;
      });
      this.$services.logger.trace('Session is logged out');
      return;
    }
    this.refreshIsSubscribedToEmail();
    this.refreshIsSubscribedToText();
    this.refreshIsSubscribedToPersonalization();
    this.errorMessage = '';
    this.setloyaltyMembershipId();
    this.setloyaltySubscriptionStartDate();
  }

  async onClickTextSubscription(): Promise<void> {
    this.isSubscribingText = true;
    this.hasSubscriptionError = false;

    try {
      if (this.subscribeToText) {
        await this.$services.customer.subscribeToMarketingChannel(MarketingChannel.Sms, MarketingContext.Marketing);
      } else {
        await this.$services.customer.unsubscribeToMarketingChannel(MarketingChannel.Sms, MarketingContext.Marketing);
      }

      this.$services.analytics.send(new AccountManagementEvent(this.subscribeToText ? InteractionTrackingType.ToggleTextOn : InteractionTrackingType.ToggleTextOff));

      this.$services.customer.setProfileSubscriptionChangeOnly(true);
      this.$services.customer.refreshCustomerProfileInfo(true, false, true);
    } catch (err) {
      this.hasSubscriptionError = true;
      this.subscribeToText = !this.subscribeToText;

      if (err instanceof CustomerError) {
        this.errorMessage = err.message;
      } else {
        throw err;
      }
    }

    this.isSubscribingText = false;
  }

  async onClickEmailSubscription(): Promise<void> {
    this.isSubscribingEmail = true;
    this.hasSubscriptionError = false;

    try {
      if (this.subscribeToEmail) {
        await this.$services.customer.subscribeToMarketingChannel(MarketingChannel.Email, MarketingContext.Marketing);
      } else {
        await this.$services.customer.unsubscribeToMarketingChannel(MarketingChannel.Email, MarketingContext.Marketing);
      }

      this.$services.analytics.send(new AccountManagementEvent(this.subscribeToEmail ? InteractionTrackingType.ToggleEmailOn : InteractionTrackingType.ToggleEmailOff));

      this.$services.customer.setProfileSubscriptionChangeOnly(true);
      this.$services.customer.refreshCustomerProfileInfo(true, false, true);
    } catch (err) {
      this.hasSubscriptionError = true;
      this.subscribeToEmail = !this.subscribeToEmail;

      if (err instanceof CustomerError) {
        this.errorMessage = err.message;
      } else {
        throw err;
      }
    }

    this.isSubscribingEmail = false;
  }

  async onClickPersonalizationSubscription(): Promise<void> {
    this.isSubscribingPersonalization = true;
    this.hasSubscriptionError = false;

    try {
      await this.$services.customer.updateCustomer({ optedIntoPersonalization: this.subscribeToPersonalization });
      this.$services.customer.setProfileSubscriptionChangeOnly(true);
      this.$services.customer.refreshCustomerProfileInfo(true, false, true);
    } catch (err) {
      this.hasSubscriptionError = true;
      this.subscribeToPersonalization = !this.subscribeToPersonalization;

      if (err instanceof CustomerError) {
        this.errorMessage = err.message;
      } else {
        throw err;
      }
    }

    this.isSubscribingPersonalization = false;
  }

  onEditInfo(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('editAccountModal', true);
    this.$services.analytics.send(new AccountPageLoadEvent('update-info'));
    this.$services.ui.firstFocus();
  }

  onClickInfoIcon(context: string) {
    let title = '';
    let body = '';
    const contentFragment = this.$services.config.getAccountOptInToolTips();

    switch (context) {
      case 'email':
        title = contentFragment.emailTitle ||
          'Email Subscription Info';
        body = contentFragment.emailBodyText ||
          'By clicking yes you are granting Chipotle permission to send you email messages at' +
          ' the email address associated with your account regarding marketing and other promotional offers.'
        break;
      case 'sms':
        title = contentFragment.smsTitle ||
          'SMS Subscription Info';
        body = contentFragment.smsBodyText ||
          'By clicking yes you are granting Chipotle permission to send you SMS (text) messages at the number' +
          ' associated with your account regarding marketing, promotional offers, and orders you may have placed. ' +
          'For more information please review our full <b><a href="/terms-of-use#mobile-terms">Mobile Terms & Conditions</a></b> section of our <b><a href="/terms-of-use">Terms of Use</a></b> to learn more.'
        break;
      case 'personalization':
        title = 'Personalized Content & Offers Info';
        body = 'By selecting yes you are granting Chipotle permission to use automated processing of your personal information' +
          ' and other data, including order history and data obtained from your interactions with Chipotle, in order to provide' +
          ' you personalized content and offers for the best experience possible. <br/><br/>Learn more in our <b><a href="/privacy-policy">Privacy Policy</a></b>.<br/><br/>'
    }

    this.$services.ui.setModalVisibility('basicError', true, {
      header: title,
      content: body,
      options: [
        {
          text: 'Ok',
          onClick: () => this.$services.ui.setModalVisibility('basicError', false),
        },
      ],
    });
  }

  onUpdatePassword(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('updatePasswordModal', true);
    this.$services.analytics.send(new AccountPageLoadEvent('update-password'));
    this.$services.ui.firstFocus();
  }

  onLeaveRewards(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);

    if (this.isCustomerStudent) {
      this.$services.ui.setModalVisibility('leaveChipotleUModal', true, {
        isLeavingRewards: true
      });
    } else {
      this.$services.ui.setModalVisibility('leaveRewardsModal', true);
    }

    this.$services.analytics.send(new AccountPageLoadEvent('leave-rewards'));
    this.$services.ui.firstFocus();
    this.$services.customer.setJoinedLoyaltyOnOrderConfirmationPage(false);
  }

  onLeaveChipotleU(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('leaveChipotleUModal', true, {
      isLeavingRewards: false
    });
    this.$services.ui.firstFocus();
  }

  onSelectTags(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('dietaryFiltersModal', true);
    this.$services.ui.firstFocus();
  }

  onClickLearnMore(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('twoStepVerificationLearnMoreModal', true);
    this.$services.analytics.send(new AccountPageLoadEvent('two-step-verification-learn-more'));
    this.$services.ui.firstFocus();
  }

  async onClickJoinRewards() {
    this.isJoiningRewards = true;
    try {
      const enrollmentOptions: EnrollmentOptions = {
        customer: this.customer,
        pageTitle: document.title,
        shouldSubscribeToMarketingEmail: this.customer?.country === CountryCode.US,
        shouldSubscribeToMarketingSMS: this.subscribeToText,
      };

      await this.$services.loyalty.enrollInLoyalty(enrollmentOptions);
      this.$services.ui.showOnboardingModal(this.$services.customer, this.customer ?? undefined);

      this.$services.customer.setProfileSubscriptionChangeOnly(true);
      await this.$services.customer.refreshCustomerProfileInfo(true, false, true);

      this.$services.adobeTarget.sendAdobeTargetExperienceRequest(this.$route.name);
    } catch (e: any) {
      // TODO-25q6xt: handle errors
      this.$services.logger.error('Something went wrong while enrolling in loyalty', { error: e });
    } finally {
      this.isJoiningRewards = false;
    }

    this.$services.analytics.send(new AccountManagementEvent('personal-preferences|join-chipotle-rewards'));
    this.$services.ui.firstFocus();
  }

  private refreshIsSubscribedToEmail(): void {
    this.subscribeToEmail = this.$services.customer.hasSubscription(MarketingChannel.Email, MarketingContext.Marketing);
  }

  private refreshIsSubscribedToText(): void {
    this.subscribeToText = this.$services.customer.hasSubscription(MarketingChannel.Sms, MarketingContext.Marketing);
  }

  private refreshIsSubscribedToPersonalization(): void {
    this.subscribeToPersonalization = this.$appStore.state.customer.optedIntoPersonalizationMessaging;
  }
}
